import { useState, useEffect, useCallback } from 'react';
import React from 'react';
import { Table, Input, Typography, Spin, Select, notification } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined } from '@ant-design/icons';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { Form } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FeedImportLine, FeedImportResponse } from '@app/models/feed_imports/FeedImportResponse';
import {
  createFeedImport,
  editFeedImport,
  showFeedImport,
  storeFeedImport,
  updateFeedImport,
} from '@app/api/feedImports.api';

interface FeedImportProps {
  id?: string;
  type: 'edit' | 'create' | 'view';
  pageTitle: string;
  editable: boolean;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setCurrentBreadcrumName: Function;
}

const { Title } = Typography;
const { Option } = Select;

const FeedImportsComponent: React.FC<FeedImportProps> = ({ type, pageTitle, editable, id }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [form] = Form.useForm();
  const { addHeaderButton, removeHeaderButton } = useHeader();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState<any[]>([]);
  const [columnHeader, setColumnHeader] = useState('Raw Materials');
  const [existingLines, setExistingLines] = useState<FeedImportLine[]>([]);

  const years = Array.from({ length: 26 }, (_, i) => new Date().getFullYear() - i);

  const fetchFeeds = useCallback(async () => {
    setLoading(true);
    try {
      let response: FeedImportResponse | undefined;
      switch (type) {
        case 'create':
          response = await createFeedImport();
          break;
        case 'edit':
        case 'view':
          if (id) {
            response = type === 'edit' ? await editFeedImport(parseInt(id)) : await showFeedImport(parseInt(id));
          }
          break;
      }

      if (response?.pr_feed_types?.length) {
        setColumnHeader('Type of Feed');
        setData(response.pr_feed_types);

        const initializedLines: FeedImportLine[] = [];
        response.pr_feed_types.forEach((parent: any) => {
          parent.children.forEach((child: any) => {
            initializedLines.push({
              pr_feed_type_id: child.id,
              qty: 0,
            } as FeedImportLine);
          });
        });

        if (response?.pr_feed_importation?.pr_feed_importation_lines?.length) {
          response.pr_feed_importation.pr_feed_importation_lines.forEach((line: any) => {
            const idx = initializedLines.findIndex((l) => l.pr_feed_type_id === line.pr_feed_type_id);
            if (idx !== -1) {
              initializedLines[idx] = { ...initializedLines[idx], ...line };
            } else {
              initializedLines.push(line);
            }
          });
        }

        setExistingLines(initializedLines);

        if (response?.pr_feed_importation?.year) {
          setSelectedYear(parseInt(response.pr_feed_importation.year));
        }
      }
    } catch (error) {
      console.error(`Error fetching Ingredients for ${type} mode:`, error);
    } finally {
      setLoading(false);
    }
  }, [type, id]);

  useEffect(() => {
    fetchFeeds();
  }, [fetchFeeds]);

  useEffect(() => {
    if (editable) {
      addHeaderButton(
        'save-button',
        <BaseButton type="primary" htmlType="submit" onClick={handleSubmit} loading={loading} size="small">
          {t('Save')}
        </BaseButton>,
      );

      addHeaderButton(
        'cancel-button',
        <BaseButton onClick={() => navigate('/feed-imports')} style={{ marginRight: '8px' }} size="small">
          {t('Cancel')}
        </BaseButton>,
      );
    }

    if (!editable && id) {
      addHeaderButton(
        'edit-button',
        <BaseButton type="primary" onClick={() => navigate(`/edit-feed-import`, { state: { id } })} size="small">
          {t('Edit')}
        </BaseButton>,
      );
    }

    return () => {
      removeHeaderButton('save-button');
      removeHeaderButton('cancel-button');
      removeHeaderButton('edit-button');
    };
  }, [form, loading, editable, id, addHeaderButton, t, navigate, removeHeaderButton]);

  const columns: ColumnsType<any> = [
    {
      title: 'Type of Feed',
      key: 'type',
      render: (_, record) => {
        // This is a parent category row
        if (!record.isChild) {
          return <strong>{record.type}</strong>;
        }
        // This is a child category row
        return <span style={{ paddingLeft: '20px' }}>{record.type}</span>;
      },
    },
    {
      title: 'Quantity (MT)',
      key: 'qty',
      align: 'center',
      render: (_, record) => {
        // Only show input for child records
        if (!record.isChild) {
          return null;
        }

        const matchingLine = existingLines.find((line) => line.pr_feed_type_id === record.id);
        const value = matchingLine ? matchingLine.qty : 0;

        if (editable) {
          return (
            <Input
              value={value}
              onChange={(e) => handleQtyChange(record.id, Number(e.target.value))}
              style={{ width: '100px' }}
              min={0}
              step={0.01}
            />
          );
        }

        return <span>{Number(value).toFixed(2)}</span>;
      },
    },
  ];

  const handleQtyChange = (id: number, value: number | null) => {
    const updatedLines = [...existingLines];
    const existingLineIndex = updatedLines.findIndex((line) => line.pr_feed_type_id === id);
    const numericValue = value ?? 0;

    if (existingLineIndex !== -1) {
      updatedLines[existingLineIndex].qty = numericValue;
    } else {
      updatedLines.push({
        pr_feed_type_id: id,
        qty: numericValue,
      } as FeedImportLine);
    }

    setExistingLines(updatedLines);
  };

  const handleSubmit = async () => {
    const formattedPayload = {
      year: selectedYear.toString(),
      pr_feed_importation_lines: existingLines.map((line) => {
        const lineData: {
          pr_feed_type_id: number;
          qty: number;
          id?: number;
        } = {
          pr_feed_type_id: line.pr_feed_type_id,
          qty: line.qty || 0,
        };

        if (type === 'edit' && line.id) {
          lineData.id = line.id;
        }

        return lineData;
      }),
    };

    console.log('Submitting payload:', formattedPayload);

    try {
      if (type === 'edit' && id) {
        await updateFeedImport(parseInt(id), formattedPayload);
      } else {
        await storeFeedImport(formattedPayload);
      }

      navigate('/feed-imports');
    } catch (error: any) {
      console.error('Error submitting data:', error);

      if (error.response?.status === 500) {
        notification.error({
          message: 'Error',
          description: `A record already exists for the year ${selectedYear}.`,
        });
      } else {
        notification.error({ message: 'Failed to submit the data' });
      }
    }
  };

  const getTableData = () => {
    const tableData: any[] = [];

    // Filter data based on search
    let filteredData = data;
    if (searchTerm) {
      filteredData = data.filter(
        (item) =>
          item.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.children.some((child: any) => child.type.toLowerCase().includes(searchTerm.toLowerCase())),
      );
    }

    filteredData.forEach((parent) => {
      // Add parent row
      tableData.push({
        key: `parent-${parent.id}`,
        id: parent.id,
        type: parent.type,
        isChild: false,
      });

      // Add all children rows
      parent.children.forEach((child: any) => {
        tableData.push({
          key: `child-${child.id}`,
          id: child.id,
          type: child.type,
          parent_id: parent.id,
          isChild: true,
        });
      });
    });

    return tableData;
  };

  return (
    <BaseCard>
      <Title level={3}>{pageTitle || 'Feed Types'}</Title>
      <div style={{ display: 'flex', marginBottom: '16px', gap: '10px' }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder={`Search ${columnHeader}...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: '300px' }}
        />
        <Select
          style={{ width: '120px' }}
          value={selectedYear}
          onChange={(value) => setSelectedYear(value)}
          disabled={type !== 'create'}
        >
          {years.map((year) => (
            <Option key={year} value={year}>
              {year}
            </Option>
          ))}
        </Select>
      </div>
      {loading ? (
        <Spin size="small" />
      ) : (
        <Table
          columns={columns}
          dataSource={getTableData()}
          rowKey="key"
          pagination={false}
          bordered
          size="small"
          locale={{ emptyText: 'No data available' }}
        />
      )}
    </BaseCard>
  );
};

export default FeedImportsComponent;
